/* global window */

import log from 'loglevel';
import sha256 from 'tiny-sha256';

import Braze from './Braze';
import PIP from './PIP';
import MediaOS from './MediaOS';
import {
  getParameterByName,
  isEmailValid,
} from './utils';


/**
 * Sync user ID before starting a Braze session.
 * @param {Braze} braze - Braze instance.
 * @param {PIP} pip - PIP instance.
 */
function syncUserVeryLocal(braze, pip) {
  if (!braze.isBrazeReady) {
    return;
  }

  braze.fetchCurrentUserData()
    .then(() => braze.changeUser(braze.userId))
    .then(() => braze.openSession())
    .then(() => pip.getUserLocationPermissionStatus())
    .then(() => pip.getUserLocation())
    .then(() => pip.getUserNeighborhoods())
    .then(() => braze.setLastKnownLocation(pip.location, pip.neighborhoods))
    .then(() => braze.pushRequest())
    .catch(error => log.error(error));
}

/**
 * Sync user ID before starting a Braze session.
 * @param {Braze} braze - Braze instance.
 * @param {PIP} pip - PIP instance.
 */
function syncUserMediaOS(braze, pip) {
  if (!braze.isBrazeReady) {
    return;
  }

  braze.fetchCurrentUserData()
    .then(() => braze.changeUser(braze.userId))
    .then(() => braze.openSession())
    .then(() => pip.getUserLocationPermissionStatus())
    .then(() => pip.getUserLocation())
    .then(() => pip.getUserNeighborhoods())
    .then(() => braze.setLastKnownLocation(pip.location, pip.neighborhoods))
    .then(() => MediaOS.updateWeather(pip.location))
    .then(() => braze.pushRequest())
    .catch(error => log.error(error));
}

/**
 * Signup user to Braze email if query params are present
 * @param {Braze} braze - Braze instance.
 */
function signUpBrazeViaQueryString(braze) {
  if (!braze.isBrazeReady) {
    return;
  }

  const email = getParameterByName('eml');
  const userId = getParameterByName('brzu');
  const groupId = getParameterByName('sub_grp_id');

  if (email && isEmailValid(email)) {
    braze.userSignUp(sha256(email), email, groupId, 'click-to-subscribe');
  }

  braze.userSignUpByHash(userId, groupId, 'click-to-subscribe');
}

/**
 * @returns {Promise}
 */
function userSignUp() {
  log.warn('Deprecation Warning: this method has been replaced by userSignUpBraze');
  return Promise.resolve();
}

/**
 * Basic user signup flow: change user and subscribe the email to the Braze list
 * @param {Braze} braze - Braze instance.
 * @param {string} email - User email.
 * @param {string} groupId - Braze email list.
 * @param {string} source - subscription source.
 * @returns {Promise}
 */
function userSignUpBraze(braze, email, groupId, source) {
  return Promise.all([
    braze.userSignUp(sha256(email), email, groupId, source),
  ]);
}

/**
 * Basic user subscribe flow: change user and send custom event
 * @param {Braze} braze - Braze instance.
 * @param {string} email - User email.
 * @param {string} eventName - subscription eventName.
 * @returns {Promise}
 */
function userSubscribeBraze(braze, email, eventName) {
  return braze.userSubscribe(sha256(email), email, eventName);
}

/**
 * Handle message events.
 * @param {Braze} braze - Braze instance.
 * @param {MessageEvent} event - Message event.
 */
function handleMessage(braze, event) {
  if (typeof event.data !== 'object') {
    return;
  }

  const {
    source,
    destination,
    method,
    parameters,
  } = event.data;

  if (destination !== 'metaphor') {
    return;
  }

  switch (method) {
    case 'brazeUserSignUp': {
      const { email, groupId, source: signUpSource } = parameters;
      braze.userSignUp(sha256(email), email, groupId, signUpSource)
        .then(() => event.source.postMessage({
          source: 'metaphor',
          destination: source,
          success: true,
        }, event.origin));
      break;
    }
    default: {
      log.warn(`Unknown method [${method}] in message.`);
    }
  }
}

/**
 * Load and initialize required scripts.
 */
function bootstrap() {
  const braze = new Braze();
  const pip = new PIP();
  const components = [braze, pip];

  if (window.METAPHOR_DISABLED) {
    return;
  }

  Promise.all(components.map(component => component.load()))
    .then(() => {
      components.map(component => component.init());

      if (window.VLCL) {
        syncUserVeryLocal(braze, pip);
      }

      if (window.HRST) {
        syncUserMediaOS(braze, pip);
        signUpBrazeViaQueryString(braze);
      }

      window.addEventListener('message', handleMessage.bind(this, braze), false);

      window.Metaphor = window.Metaphor || {};
      window.Metaphor.signUp = userSignUp.bind(this);
      window.Metaphor.signUpBraze = userSignUpBraze.bind(this, braze);
      window.Metaphor.subscribeBraze = userSubscribeBraze.bind(this, braze);
    })
    .catch(error => log.error(error));
}

export default bootstrap;



// WEBPACK FOOTER //
// ./src/bootstrap.js